import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import type {
    CreateFuelTypeRequest,
    FuelType,
    FuelTypesRequest,
    FuelTypesResponse,
    UpdateFuelTypeRequest,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/fuel-type.api.ts';
import {
    createApiFuelType,
    deleteApiFuelType,
    getApiFuelTypes,
    updateApiFuelType,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/api/fuel-type.api.ts';
import { extractError } from '@utils/slice.ts';

export interface FuelTypesError {
    message?: string;
    [k: string]: any;
}

export interface FuelTypesState {
    error: FuelTypesError | null;

    commonFuelTypes: FuelTypesResponse | null;
    fuelTypes: FuelType[];
    pagination: CommonTablePaginatedResponse;

    updatedFuelType: FuelType | null;
    currentGetRequestId: string | null;

    isGetFuelTypesPending: boolean;
    isCreateFuelTypePending: boolean;
    isUpdateFuelTypePending: boolean;
    isDeleteFuelTypePending: boolean;
}

const initialState: FuelTypesState = {
    error: null,

    commonFuelTypes: null,
    fuelTypes: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },

    updatedFuelType: null,
    currentGetRequestId: null,

    isGetFuelTypesPending: false,
    isCreateFuelTypePending: false,
    isUpdateFuelTypePending: false,
    isDeleteFuelTypePending: false,
};

export const getFuelTypes = createAsyncThunk<
    FuelTypesResponse,
    { clientId: number; tenantAlias: string; payload: FuelTypesRequest },
    { rejectValue: FuelTypesError }
>('adminCenterFuelTypes/getFuelTypes', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    getApiFuelTypes(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const createFuelType = createAsyncThunk<
    FuelType,
    { clientId: number; tenantAlias: string; payload: CreateFuelTypeRequest },
    { rejectValue: FuelTypesError }
>('adminCenterFuelTypes/createFuelType', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiFuelType(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateFuelType = createAsyncThunk<
    FuelType,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateFuelTypeRequest },
    { rejectValue: FuelTypesError }
>('adminCenterFuelTypes/updateFuelType', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiFuelType(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteFuelType = createAsyncThunk<
    FuelType,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: FuelTypesError }
>('adminCenterFuelTypes/deleteFuelType', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiFuelType(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

const fuelTypesSlice = createSlice({
    name: 'adminCenterFuelTypes',
    initialState,
    reducers: {
        resetFuelTypesState: () => initialState,
    },
    extraReducers: (builder) => {
        builder.addCase(getFuelTypes.pending, (state, action) => {
            state.isGetFuelTypesPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getFuelTypes.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetFuelTypesPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonFuelTypes = action.payload;
            state.fuelTypes = data ?? [];
            state.pagination = pagination;
        });
        builder.addCase(getFuelTypes.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetFuelTypesPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createFuelType.pending, (state) => {
            state.isCreateFuelTypePending = true;
            state.error = null;
        });
        builder.addCase(createFuelType.fulfilled, (state, action) => {
            state.isCreateFuelTypePending = false;
            state.updatedFuelType = action.payload;
        });
        builder.addCase(createFuelType.rejected, (state, action) => {
            state.isCreateFuelTypePending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateFuelType.pending, (state) => {
            state.isUpdateFuelTypePending = true;
            state.error = null;
        });
        builder.addCase(updateFuelType.fulfilled, (state, action) => {
            state.isUpdateFuelTypePending = false;
            state.updatedFuelType = action.payload;
        });
        builder.addCase(updateFuelType.rejected, (state, action) => {
            state.isUpdateFuelTypePending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteFuelType.pending, (state) => {
            state.isDeleteFuelTypePending = true;
            state.error = null;
        });
        builder.addCase(deleteFuelType.fulfilled, (state, action) => {
            state.isDeleteFuelTypePending = false;
            state.updatedFuelType = action.payload;
            state.fuelTypes = state.fuelTypes.filter((t) => t.id !== action.payload.id);
        });
        builder.addCase(deleteFuelType.rejected, (state, action) => {
            state.isDeleteFuelTypePending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { fuelType } = action.payload;

            if (!fuelType) return;

            const { data, pagination } = fuelType;
            state.commonFuelTypes = fuelType;
            state.fuelTypes = data ?? [];
            state.pagination = pagination;
        });
    },
});

export const { resetFuelTypesState } = fuelTypesSlice.actions;
export default fuelTypesSlice.reducer;
